/**
 * /projects — refined-classic список проектов (см. task 13/3.1).
 *
 * Header: breadcrumb «ПОРТФЕЛЬ / ПРОЕКТЫ» → title «Проекты» → subtitle.
 * Toolbar: Search + Статус-dropdown (incl. virtual «Под риском»), счётчик
 * «N ПРОЕКТОВ» справа. Таблица 6 колонок (Название+code, Заказчик, Статус,
 * Менеджер, Сроки, Прогресс). Пагинация «from–to из total / Назад / Вперёд».
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";

import { projectsRoute } from "@/router";
import { useCanEditProjects } from "@/store/session";
import { AppShell } from "@/layout/AppShell";
import { useProjects } from "@/hooks/useProjects";
import { useUsersLookup } from "@/hooks/useUnits";
import type { ProjectListItem, ProjectStatus } from "@/api/projects";
import { SearchInput } from "@/components/ui/search-input";
import { Button } from "@/components/ui/button";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { PageHeader, PageHeaderSpacer, PageTitle } from "@/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  SortableTableHead,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ProjectStatusBadge } from "@/components/ProjectStatusBadge";
import { formatDate } from "@/lib/datetime";

type StatusFilter = ProjectStatus | "all" | "at_risk";

const STATUS_OPTIONS: StatusFilter[] = [
  "all",
  "planned",
  "in_progress",
  "on_hold",
  "completed",
  "cancelled",
  "at_risk",
];

export function ProjectsPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canCreate = useCanEditProjects();

  const search_ = projectsRoute.useSearch();
  const navigateRoute = projectsRoute.useNavigate();
  const statusFilter: StatusFilter = (search_.status as StatusFilter | undefined) ?? "all";
  const showArchived = search_.archived === true;
  const setStatusFilter = (next: StatusFilter): void => {
    void navigateRoute({
      search: (prev) => ({ ...prev, status: next === "all" ? undefined : next }),
      replace: true,
    });
  };
  const toggleArchived = (): void => {
    void navigateRoute({
      search: (prev) => ({ ...prev, archived: showArchived ? undefined : true }),
      replace: true,
    });
  };

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "code", desc: false }]);

  // `at_risk` — derived фильтр; на бэк его не шлём, оставляем status=null и
  // отфильтруем клиентски по `is_at_risk`.
  const backendStatus: ProjectStatus | null =
    statusFilter === "all" || statusFilter === "at_risk" ? null : statusFilter;

  const { data, isLoading, isError, error, refetch } = useProjects({
    status: backendStatus,
    archivedOnly: showArchived,
    limit: 200,
  });

  const usersLookup = useUsersLookup();
  const managerById = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of usersLookup.data ?? []) map.set(u.id, u.display_name);
    return map;
  }, [usersLookup.data]);

  const filtered = useMemo(() => {
    let rows = (data ?? []).filter((project) => project.is_archived === showArchived);
    if (statusFilter === "at_risk") {
      rows = rows.filter((p) => p.is_at_risk);
    }
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (p) =>
        p.name.toLowerCase().includes(needle) ||
        p.customer_name.toLowerCase().includes(needle) ||
        (p.code?.toLowerCase().includes(needle) ?? false),
    );
  }, [data, search, showArchived, statusFilter]);

  const columns = useMemo<ColumnDef<ProjectListItem>[]>(
    () => [
      {
        // Номер проекта — отдельная сортируемая колонка, первой слева (задача 100
        // §4.1): номер часто в ходу больше названия, «вторым этажом» под именем он
        // был невзрачным. Поиск по номеру уже покрыт общим Search (см. `filtered`).
        id: "code",
        header: t("projects.columns.code"),
        accessorKey: "code",
        cell: ({ row }) =>
          row.original.code ? (
            <span
              data-testid={`project-row-code-${row.original.id}`}
              className="font-mono whitespace-nowrap"
            >
              {row.original.code}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "name",
        header: t("projects.columns.name"),
        accessorKey: "name",
        cell: ({ row }) => (
          <Link
            to="/projects/$projectId"
            params={{ projectId: String(row.original.id) }}
            data-testid={`project-link-${row.original.id}`}
            className="text-primary font-medium hover:underline"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: "customer_name",
        header: t("projects.columns.customer"),
        accessorKey: "customer_name",
        cell: ({ row }) =>
          row.original.customer_name || <span className="text-muted-foreground">—</span>,
      },
      {
        id: "status",
        header: t("projects.columns.status"),
        accessorKey: "status",
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <ProjectStatusBadge
              status={row.original.status}
              atRisk={row.original.is_at_risk}
              overdue={row.original.is_overdue}
              plannedEnd={row.original.planned_end}
            />
            {row.original.is_archived && (
              <Badge tone="neutral" data-testid={`project-row-archived-${row.original.id}`}>
                {t("projects.archivedBadge")}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: "manager",
        header: t("projects.columns.manager"),
        accessorFn: (row) =>
          row.manager_user_id != null ? (managerById.get(row.manager_user_id) ?? "") : "",
        cell: ({ row }) => {
          const id = row.original.manager_user_id;
          const name = id != null ? managerById.get(id) : null;
          return name ? (
            <span data-testid={`project-row-manager-${row.original.id}`}>{name}</span>
          ) : (
            <span className="text-muted-foreground">{t("projects.noManager")}</span>
          );
        },
      },
      {
        id: "dates",
        header: t("projects.columns.dates"),
        accessorFn: (row) => row.planned_start ?? "",
        cell: ({ row }) => {
          const start = row.original.planned_start;
          const end = row.original.planned_end;
          if (!start && !end) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="tabular-nums">
              {t("projects.dateRange", {
                start: start ? formatDate(start, i18n.resolvedLanguage) : "—",
                end: end ? formatDate(end, i18n.resolvedLanguage) : "—",
              })}
            </span>
          );
        },
      },
      {
        id: "progress",
        header: t("projects.columns.progress"),
        accessorFn: (row) => row.progress_pct,
        cell: ({ row }) => (
          <span
            data-testid={`project-row-progress-${row.original.id}`}
            className="flex w-full items-center gap-3"
          >
            <Progress
              value={row.original.progress_pct}
              tone={row.original.is_at_risk ? "danger" : "default"}
              className="h-2 min-w-0 flex-1"
            />
            <span className="tabular-nums w-10 text-right">{row.original.progress_pct}%</span>
          </span>
        ),
      },
    ],
    [t, i18n.resolvedLanguage, managerById],
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 25 } },
  });

  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;
  const totalRows = filtered.length;
  const rangeFrom = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeTo = Math.min((pageIndex + 1) * pageSize, totalRows);

  return (
    <AppShell>
      <nav
        className="text-muted-foreground mb-3 flex items-center gap-1 text-[11px] font-medium tracking-[0.12em] uppercase"
        aria-label={t("nav.breadcrumb")}
        data-testid="projects-breadcrumb"
      >
        <span>{t("projects.breadcrumb.portfolio")}</span>
        <span aria-hidden="true">/</span>
        <span className="text-foreground">{t("projects.breadcrumb.projects")}</span>
      </nav>

      <PageHeader>
        <PageTitle data-testid="projects-title">{t("projects.title")}</PageTitle>
        <PageHeaderSpacer />
        {canCreate && (
          <Button asChild data-testid="projects-new">
            <Link to="/projects/new">
              <Plus className="h-4 w-4" />
              {t("projects.newCta")}
            </Link>
          </Button>
        )}
      </PageHeader>
      <p className="text-muted-foreground mt-1 mb-4 text-sm">{t("projects.subtitle")}</p>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          data-testid="projects-search"
          aria-label={t("projects.searchAria")}
          placeholder={t("projects.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          containerClassName="max-w-xs"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="projects-status-filter">
              {t("projects.status")}: {t(`projects.statusValues.${statusFilter}`)}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {STATUS_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt}
                data-testid={`projects-status-option-${opt}`}
                onSelect={() => setStatusFilter(opt)}
              >
                {t(`projects.statusValues.${opt}`)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="outline"
          aria-pressed={showArchived}
          onClick={toggleArchived}
          data-testid="projects-archived-toggle"
          className={showArchived ? "border-[var(--rule-strong)] text-foreground" : undefined}
        >
          {t("projects.showArchived")}
        </Button>
        <div className="flex-1" />
        <span
          data-testid="projects-counter"
          className="text-muted-foreground text-[11px] font-medium tracking-[0.12em] uppercase tabular-nums"
        >
          {totalRows} {t("projects.counterUnit")}
        </span>
      </div>

      <div
        className="mt-3 w-fit min-w-full rounded-[var(--radius-card)] border bg-[var(--bg-panel-0)]"
        data-testid="projects-table-card"
      >
        <Table containerClassName="overflow-x-visible">
          {/* sticky-шапка прижата под TopBar: отрицательный top гасит верхний
              p-6 у <main>-скроллера (иначе в полосе паддинга просвечивают строки). */}
          <TableHeader className="sticky top-[calc(var(--spacing)*-6)] z-20 border-b border-[var(--rule-line)] bg-[var(--bg-panel-0)]">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <SortableTableHead
                    key={h.id}
                    sortable={h.column.getCanSort()}
                    direction={h.column.getIsSorted()}
                    onToggle={h.column.getToggleSortingHandler()}
                    align={h.column.id === "progress" ? "right" : "left"}
                    data-testid={`projects-sort-${h.column.id}`}
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </SortableTableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-muted-foreground py-6 text-center"
                >
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-6 text-center">
                  <ApiErrorNotice
                    error={error}
                    fallbackMessage={t("common.apiError.fallback")}
                    className="text-destructive"
                  />
                  <Button variant="ghost" size="sm" className="ml-2" onClick={() => void refetch()}>
                    {t("common.retry")}
                  </Button>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-muted-foreground py-6 text-center"
                >
                  {t("projects.empty")}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-testid={`project-row-${row.original.id}`}
                  className={
                    row.original.is_archived ? "cursor-pointer opacity-55" : "cursor-pointer"
                  }
                  onClick={() => {
                    const id = row.original.id;
                    if (id !== null && id !== undefined) {
                      void navigate({
                        to: "/projects/$projectId",
                        params: { projectId: String(id) },
                      });
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isProgress = cell.column.id === "progress";
                    return (
                      <TableCell key={cell.id} className={isProgress ? "text-right" : undefined}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* закреплённый футер пагинации; отрицательный bottom гасит нижний
            p-6 у <main>-скроллера (симметрично sticky-шапке). */}
        <div className="sticky bottom-[calc(var(--spacing)*-6)] z-20 flex items-center justify-end gap-2 rounded-b-[var(--radius-card)] border-t border-[var(--rule-line)] bg-[var(--bg-panel-0)] px-2 py-2 text-sm">
          <span
            className="text-muted-foreground tabular-nums"
            data-testid="projects-page-indicator"
          >
            {t("projects.pageRange", { from: rangeFrom, to: rangeTo, total: totalRows })}
          </span>
          <Button
            variant="outline"
            size="sm"
            data-testid="projects-prev"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            {t("projects.prev")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="projects-next"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            {t("projects.next")}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
